import {
  type UpdateChannel,
  UpdateEventType,
  type UpdateResult,
} from '@electron/shared';
import { formatTimezone } from '@littlebird/ui-utils';
import { useMutation } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { TextIconButton } from '@/components/ui/text-icon-button';
import { Tooltip } from '@/components/ui/Tooltip';
import { electronApis } from '@/features/electron-api';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { logoutWithMeetingGuard } from '@/features/meetings/meeting-guard';
import { EmailSchema } from '@/features/user/user.types';
import { withUIDelay } from '@/lib/animate';
import { logError } from '@/lib/errorLogging';
import { settingsStore } from '@/stores/settings.store';
import { updateStore } from '@/stores/updateStore';
import { userStore } from '@/stores/user.store';
import { listenWithAbortIPC } from '@/utils/abort';
import { collectUserConfig, setUserProperties } from '@/utils/analytics';
import { AppCapability, inBrowser, isCapable } from '@/utils/capabilities';
import { navigate } from '@/utils/navigation-service';

import { SettingsSection } from '../settings-section';

const toChannelName = (channel: UpdateChannel | null) => {
  if (!channel) return null;
  if (channel === 'alpha') return 'Alpha';
  if (channel === 'beta') return 'Beta';
  if (channel === 'latest') return 'Stable';
  return null;
};

export const GeneralSettings = () => {
  const hasFeature = useFeature();

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>General</SettingsSection.Title>
      </SettingsSection.Header>
      <NameInput />
      <AliasesInput />
      <TimezoneDisplay />
      <EmailAddress />
      {hasFeature(FEATURES.DATA_SHARING) && (
        <SettingsSection.Group>
          <DataSharingToggle />
        </SettingsSection.Group>
      )}

      {isCapable(AppCapability.DesktopExclusive) && <VersionDetails />}
      <Logout />
    </SettingsSection.Root>
  );
};

const NameInput = observer(() => {
  const user = userStore.user;
  const [name, setName] = useState(user?.name ?? '');

  return (
    <SettingsSection.Item direction="vertical" searchId="general.name">
      <SettingsSection.ItemLabel>
        What should Littlebird call you?
      </SettingsSection.ItemLabel>
      <Input
        placeholder="e.g. Robert"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          if (name.trim().length !== 0 && name !== user?.name && user) {
            userStore.syncUpdate({
              name: name,
            });
            collectUserConfig();
          }
        }}
      />
    </SettingsSection.Item>
  );
});

const AliasesInput = observer(() => {
  const user = userStore.user;
  const [alias, setAlias] = useState(user?.aliases ?? '');

  return (
    <SettingsSection.Item direction="vertical" searchId="general.aliases">
      <SettingsSection.ItemLabel>Aliases</SettingsSection.ItemLabel>
      <Input
        placeholder="e.g. Rob, Robby, robert2025"
        value={alias}
        onChange={(e) => setAlias(e.target.value)}
        onBlur={() => {
          if (alias.trim().length !== 0 && alias !== user?.aliases && user) {
            userStore.syncUpdate({
              aliases: alias,
            });
            collectUserConfig();
          }
        }}
      />
      <SettingsSection.ItemDescription>
        Include your nicknames, online handles, and other identifiers, separated
        by commas
      </SettingsSection.ItemDescription>
    </SettingsSection.Item>
  );
});

const TimezoneDisplay = observer(() => {
  const timezone = userStore.user?.timezone;
  const sendDeviceTimezone = settingsStore.sendDeviceTimezone;

  return (
    <SettingsSection.Group title="Timezone">
      <SettingsSection.Item direction="vertical" searchId="general.timezone">
        <SettingsSection.ItemLabel>Current timezone</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          {timezone ? (
            <>
              Littlebird has your timezone as{' '}
              <span className="text-foreground">
                {formatTimezone(timezone)}
              </span>
              . Detected from your devices.
            </>
          ) : (
            'Littlebird timezone unknown.'
          )}
        </SettingsSection.ItemDescription>
        {!inBrowser() && (
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => electronApis.system.openSystemDateSettings()}
          >
            Open system date &amp; time settings
          </Button>
        )}
      </SettingsSection.Item>
      <SettingsSection.Seperator />
      <SettingsSection.Item searchId="general.device-timezone">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Use this device to set my timezone
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Turn this off on devices that travel or aren't where you live, so
            they don't change your timezone.
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <SettingsSection.Switch
          checked={sendDeviceTimezone}
          onCheckedChange={(v) => settingsStore.setSendDeviceTimezone(v)}
        />
      </SettingsSection.Item>
    </SettingsSection.Group>
  );
});

const validateEmailChange = (
  newEmail: string,
  currentEmail: string
): string | null => {
  const result = EmailSchema.safeParse(newEmail);
  if (!result.success) {
    return (
      result.error.issues[0]?.message || 'Please enter a valid email address'
    );
  }

  const parsedEmail = result.data;
  if (parsedEmail === currentEmail.toLowerCase()) {
    return 'New email must be different from current email';
  }
  return null;
};

const EmailAddress = observer(() => {
  const user = userStore.user;
  const [newEmail, setNewEmail] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  const { mutate: initiateEmailChange, isPending: isInitiating } = useMutation({
    mutationKey: ['initiate-email-change'],
    mutationFn: async (email: string) => {
      return backendApis.user.initiateEmailChange(email);
    },
    onSuccess: () => {
      toast.success('Verification code sent', {
        description: `A verification code has been sent to ${newEmail}`,
      });
      navigate({
        to: '/change-email/verify',
        search: { email: newEmail },
      });
      setIsChangingEmail(false);
      setNewEmail('');
    },
    onError: (error: any) => {
      toast.error('Failed to send verification code', {
        description:
          error?.response?.data?.detail?.user_error_message || error?.message,
      });
    },
  });

  const handleInitiateEmailChange = () => {
    if (!user) return;
    const error = validateEmailChange(newEmail, user.email);
    if (error) {
      toast.error(error);
      return;
    }
    initiateEmailChange(newEmail);
  };

  if (isChangingEmail) {
    return (
      <YStack className="gap-2">
        <Input
          label="New Email Address"
          info="Enter your new email address"
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value.trim().toLowerCase())}
          placeholder="new@example.com"
          disabled={isInitiating}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isInitiating) {
              handleInitiateEmailChange();
            }
          }}
        />
        <XStack className="gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setIsChangingEmail(false);
              setNewEmail('');
            }}
            disabled={isInitiating}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleInitiateEmailChange}
            isLoading={isInitiating}
            disabled={!newEmail || isInitiating}
          >
            Send Code
          </Button>
        </XStack>
      </YStack>
    );
  }

  if (!user) return null;

  return (
    <SettingsSection.Item
      searchId="general.email"
      className="justify-between items-end gap-2"
    >
      <Input label="Email" value={user.email} disabled />
      <Tooltip label="Change your email address">
        <TextIconButton.Root
          onClick={() => setIsChangingEmail(true)}
          className="size-10 mt-[2px]"
        >
          <TextIconButton.Icon>
            <Icons.squarePen />
          </TextIconButton.Icon>
        </TextIconButton.Root>
      </Tooltip>
    </SettingsSection.Item>
  );
});

const Logout = () => {
  const [isSigningOut, setIsSigningOut] = useState(false);

  const signOut = async () => {
    if (isSigningOut) return;

    setIsSigningOut(true);

    try {
      withUIDelay(async () => await logoutWithMeetingGuard('manual'));
    } catch (_error) {
      toast.error('Failed to sign out. Please try again.');
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <SettingsSection.Item searchId="general.sign-out">
      <Button
        variant="secondary"
        title="Sign Out"
        isLoading={isSigningOut}
        loadingText="Signing out..."
        tooltip={!isSigningOut ? 'Sign out from Littlebird' : 'Signing out...'}
        onClick={signOut}
      >
        Sign Out
      </Button>
    </SettingsSection.Item>
  );
};

const VersionDetails = observer(() => {
  const [currentVersion, setCurrentVersion] = useState('');
  const [updateResult, setUpdateResult] = useState<UpdateResult | null>(null);

  const [currentBuildChannel, setCurrentBuildChannel] = useState<string | null>(
    null
  );
  const [userSubscribedChannel, setUserSubscribedChannel] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (inBrowser()) return;
    electronApis.system.checkForUpdate().then(setUpdateResult);
    electronApis.system.getCurrentVersionChannel().then((channel) => {
      setCurrentBuildChannel(toChannelName(channel));
    });
    electronApis.system.getUserSubscribedChannel().then((channel) => {
      setUserSubscribedChannel(toChannelName(channel));
    });
  }, []);

  useEffect(() => {
    if (inBrowser()) return;
    const fetchVersion = async () => {
      try {
        const version = await electronApis.system.getAppVersion();
        setCurrentVersion(version);
      } catch (error) {
        logError(error, 'Failed to fetch version', {
          context: 'Settings',
        });
      }
    };

    fetchVersion();
  }, []);

  useEffect(() => {
    if (inBrowser()) return;
    const controller = new AbortController();
    listenWithAbortIPC(
      UpdateEventType.UpdateNotAvailable,
      () => {
        setUpdateResult(null);
      },
      controller.signal
    );
    return () => controller.abort();
  }, []);

  const storeHasUpdate = !!(
    updateStore.state.availableVersion &&
    updateStore.state.availableVersion !== updateStore.state.currentVersion
  );
  const isUpdateAvailable =
    (updateResult?.updateInfo?.version
      ? updateResult.updateInfo.version !== currentVersion
      : false) || storeHasUpdate;
  const newVersion =
    updateResult?.updateInfo?.version ||
    updateStore.state.availableVersion ||
    currentVersion;

  return (
    <SettingsSection.Item searchId="general.app-version">
      <YStack className="gap-1 text-sm text-foreground">
        <p className="text-foreground text-sm gap-2 items-center">
          Littlebird v{currentVersion}
          {currentBuildChannel && currentBuildChannel !== 'Stable' && (
            <span className="text-foreground text-sm">
              {' '}
              ({currentBuildChannel})
            </span>
          )}
        </p>
        <p className="text-foreground-muted text-xs inline-flex gap-1.5 items-center">
          {isUpdateAvailable ? (
            <>
              New version available: v{newVersion}
              <Badge variant="success">New</Badge>
            </>
          ) : (
            <>You are on the latest version</>
          )}
        </p>
        {userSubscribedChannel && userSubscribedChannel !== 'Stable' && (
          <p className="text-foreground-muted text-xs inline-flex gap-1.5 items-center">
            Channel: {userSubscribedChannel}
          </p>
        )}
      </YStack>

      {isUpdateAvailable && <UpdateActionButton />}
    </SettingsSection.Item>
  );
});

const UpdateActionButton = observer(() => {
  if (updateStore.isUpdateReady) {
    return (
      <Button onClick={() => updateStore.installUpdate()} variant="secondary">
        Install and Restart
      </Button>
    );
  }

  if (updateStore.state.isDownloading) {
    return (
      <Button variant="secondary" disabled>
        Downloading... {(updateStore.state.downloadProgress ?? 0).toFixed(2)}%
      </Button>
    );
  }

  return (
    <Button
      onClick={() => void updateStore.downloadUpdate()}
      variant="secondary"
    >
      {updateStore.state.error ? 'Retry Download' : 'Download Update'}
    </Button>
  );
});

const DataSharingToggle = observer(() => {
  const user = userStore.user;

  const handleToggle = (enabled: boolean) => {
    if (!user) return;
    userStore.syncUpdate({
      feature_flags: {
        ...(user.feature_flags ?? {}),
        data_sharing_enabled: enabled,
      },
    });
    setUserProperties({
      data_sharing_enabled: enabled,
    });
  };

  return (
    <SettingsSection.Item searchId="general.share-usage-data">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Share usage data</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Help improve Littlebird by sharing usage data
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={user?.feature_flags?.data_sharing_enabled ?? false}
        onCheckedChange={handleToggle}
      />
    </SettingsSection.Item>
  );
});
